import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Download } from "lucide-react";
import { Input } from "@/components/ui/input";
import { adminApi, aErr, downloadCsv, fdate, fmt } from "@/admin/adminApi";
import { CameroonMap } from "@/admin/CameroonMap";
import { can, useAdmin } from "@/admin/AdminLayout";

const useLoad = (path, deps = []) => {
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    adminApi.get(path).then((r) => setData(r.data)).catch((e) => toast.error(aErr(e)));
  }, [path]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(load, [load, ...deps]); // eslint-disable-line react-hooks/exhaustive-deps
  return [data, load];
};

const act = async (fn, ok = "Fait", after) => {
  try {
    await fn();
    toast.success(ok);
    if (after) after();
  } catch (e) {
    toast.error(aErr(e));
  }
};

const ask = (label) => {
  const r = window.prompt(label);
  return r && r.trim().length >= 3 ? r.trim() : null;
};

const H = ({ children, csv }) => (
  <div className="mb-5 flex items-center justify-between gap-3">
    <h1 className="font-display text-2xl font-bold">{children}</h1>
    {csv && <button onClick={() => downloadCsv(csv).catch((e) => toast.error(aErr(e)))} className="flex h-10 items-center gap-2 rounded-full border border-white/15 px-4 text-sm" data-testid={`export-${csv}`}><Download className="h-4 w-4" /> CSV</button>}
  </div>
);

const Btn = ({ children, onClick, tone = "amber", testId }) => (
  <button onClick={onClick} data-testid={testId}
    className={`h-9 rounded-full px-3 text-xs font-semibold ${tone === "red" ? "border border-red-500/40 text-red-300" : "bg-amber-500 text-[#0B0D12]"}`}>{children}</button>
);

const Table = ({ cols, rows, testId }) => (
  <div className="surface overflow-x-auto" data-testid={testId}>
    <table className="w-full min-w-[640px] text-left text-sm">
      <thead className="text-xs uppercase text-slate-500"><tr>{cols.map(([h]) => <th key={h} className="px-3 py-3">{h}</th>)}</tr></thead>
      <tbody>{(rows || []).map((r, i) => <tr key={r.id || i} className="border-t border-white/5">{cols.map(([h, f]) => <td key={h} className="px-3 py-2.5 align-top">{f(r)}</td>)}</tr>)}</tbody>
    </table>
    {rows && !rows.length && <p className="p-6 text-center text-sm text-slate-500">Aucun élément.</p>}
  </div>
);

const Card = ({ label, value, testId }) => (
  <div className="surface p-4"><p className="text-[11px] uppercase tracking-wider text-slate-500">{label}</p><p className="mt-1 font-mono text-lg font-semibold" data-testid={testId}>{value}</p></div>
);

const KYC_LABEL = { none: "Non renseignée", pending: "À confirmer", approved: "Confirmée", rejected: "Refusée" };
const KYC_CLS = { none: "text-slate-400", pending: "text-amber-300", approved: "text-emerald-400", rejected: "text-red-400" };
const DOC_LABEL = { cni: "Carte d'identité", passport: "Passeport", permit: "Permis de conduire" };
const kycText = (s) => <span className={`font-semibold ${KYC_CLS[s || "none"]}`}>{KYC_LABEL[s || "none"]}</span>;

const decideKyc = (uid, decision, after) => {
  if (decision === "approved") {
    if (window.confirm("Confirmer l'identité de ce joueur ? Vérifiez que le nom et le numéro correspondent à la pièce reçue.")) {
      act(() => adminApi.post(`/players/${uid}/kyc`, { decision }), "Identité confirmée", after);
    }
    return;
  }
  const r = ask("Motif du refus");
  if (r) act(() => adminApi.post(`/players/${uid}/kyc`, { decision, reason: r }), "Identité refusée", after);
};

const Field = ({ label, value, testId }) => (
  <div className="rounded-xl bg-white/[0.04] p-3"><p className="text-[11px] uppercase tracking-wider text-slate-500">{label}</p><p className="mt-1 break-words font-semibold" data-testid={testId}>{value || "—"}</p></div>
);

const PlayerMessage = ({ id }) => {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const send = async () => {
    try {
      await adminApi.post(`/players/${id}/message`, { title, body });
      toast.success("Message envoyé dans la messagerie du joueur");
      setTitle(""); setBody("");
    } catch (e) { toast.error(aErr(e)); }
  };
  return (
    <div className="surface space-y-3 p-4" data-testid="pd-message-section">
      <p className="font-semibold">Envoyer un message au joueur</p>
      <p className="text-xs text-slate-400">Il le lira dans sa messagerie (lecture seule, effacé après 30 jours). L'envoi est inscrit au journal d'audit.</p>
      <Input value={title} maxLength={80} onChange={(e) => setTitle(e.target.value)} placeholder="Titre" className="h-11 border-white/10 bg-white/5" data-testid="pd-message-title" />
      <textarea value={body} maxLength={1000} onChange={(e) => setBody(e.target.value)} placeholder="Message" rows={4} className="w-full rounded-md border border-white/10 bg-white/5 p-3 text-sm" data-testid="pd-message-body" />
      <button type="button" disabled={title.trim().length < 2 || body.trim().length < 2} onClick={send} className="h-10 rounded-lg bg-amber-500 px-4 text-sm font-semibold text-[#0B0D12] disabled:opacity-40" data-testid="pd-message-send">Envoyer</button>
    </div>
  );
};

const KycPanel = ({ d, id, reload, admin }) => {
  const u = d.user;
  const k = d.kyc;
  const wa = u.phone ? `https://wa.me/${u.phone.replace(/\D/g, "")}?text=${encodeURIComponent(`Bonjour ${u.pseudo}, ici l'équipe BKMPLAY. Pour valider votre retrait, merci d'envoyer ici la photo recto et verso de votre pièce d'identité.`)}` : null;
  return (
    <section className="surface space-y-4 border border-amber-500/20 p-5" data-testid="pd-kyc-box">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-display text-lg font-bold">Vérification d'identité</h2>
        <span data-testid="pd-kyc-status">{kycText(u.kyc_status)}</span>
      </div>
      {!k ? <p className="text-sm text-slate-400" data-testid="pd-kyc-empty">Le joueur n'a pas encore renseigné sa pièce d'identité.</p> : (
        <>
          <p className="text-xs text-slate-400">Comparez ces informations avec la pièce que le joueur vous envoie sur WhatsApp.</p>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            <Field label="Nom complet déclaré" value={k.full_name} testId="pd-kyc-name" />
            <Field label="Type de pièce" value={DOC_LABEL[k.doc_type] || k.doc_type} testId="pd-kyc-type" />
            <Field label="Numéro de la pièce" value={<span className="font-mono">{k.doc_number}</span>} testId="pd-kyc-number" />
            <Field label="Région" value={k.region || "Non renseignée"} testId="pd-kyc-region" />
            <Field label="Date de naissance (inscription)" value={u.birth_date} testId="pd-kyc-birth" />
            <Field label="Téléphone" value={u.phone} testId="pd-kyc-phone" />
            <Field label="Envoyé le" value={fdate(k.submitted_at)} testId="pd-kyc-date" />
          </div>
          {u.kyc_reason && <p className="text-sm text-red-300">Motif du dernier refus : {u.kyc_reason}</p>}
        </>
      )}
      {can(admin, "support") && (
        <div className="flex flex-wrap gap-2">
          {wa && <a href={wa} target="_blank" rel="noopener noreferrer" className="inline-flex h-11 items-center rounded-full bg-emerald-600 px-5 text-sm font-semibold text-white hover:bg-emerald-500" data-testid="pd-kyc-whatsapp">Demander les pièces sur WhatsApp</a>}
          {k && u.kyc_status === "pending" && <>
            <button onClick={() => decideKyc(id, "approved", reload)} className="h-11 rounded-full bg-amber-500 px-5 text-sm font-bold text-[#0B0D12] hover:bg-amber-400" data-testid="pd-kyc-approve">Confirmer l'identité</button>
            <button onClick={() => decideKyc(id, "rejected", reload)} className="h-11 rounded-full border border-red-500/40 px-5 text-sm font-semibold text-red-300" data-testid="pd-kyc-reject">Refuser</button>
          </>}
        </div>
      )}
    </section>
  );
};

export const Dashboard = () => {
  const [period, setPeriod] = useState("day");
  const [d] = useLoad(`/dashboard?period=${period}`, [period]);
  if (!d) return null;
  const p = d.period;
  return (
    <div data-testid="admin-dashboard">
      <H>Tableau de bord</H>
      <div className="mb-4 grid grid-cols-3 gap-3">
        <Card label="Joueurs connectés" value={d.live.online} testId="dash-online" />
        <Card label="Parties en cours (3 jeux)" value={d.live.games_in_progress} testId="dash-live-games" />
        <Card label="En file" value={Object.values(d.live.queues).reduce((a, b) => a + b, 0)} testId="dash-queued" />
      </div>
      {Object.keys(d.live.queues).length > 0 && <p className="mb-4 text-xs text-slate-400">{Object.entries(d.live.queues).map(([k, c]) => `${k.split(":")[0]} joueurs · ${fmt(+k.split(":")[1])} : ${c}`).join(" — ")}</p>}
      <div className="mb-4 flex gap-2">{["day", "week", "month"].map((x) => <button key={x} onClick={() => setPeriod(x)} className={`h-9 rounded-full px-4 text-sm ${period === x ? "bg-amber-500 text-[#0B0D12]" : "border border-white/10"}`} data-testid={`dash-period-${x}`}>{{ day: "Jour", week: "Semaine", month: "Mois" }[x]}</button>)}</div>
      <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Card label={`Parties terminées (Dés ${p.games_by_game?.dice ?? 0} · Ludo ${p.games_by_game?.ludo ?? 0} · Dames ${p.games_by_game?.dames ?? 0})`} value={p.games} testId="dash-games" /><Card label="Volume misé" value={fmt(p.volume)} testId="dash-volume" />
        <Card label="Commissions" value={fmt(p.commission)} testId="dash-commission" /><Card label="Solde détenu joueurs" value={fmt(p.held_for_players)} testId="dash-held" />
        <Card label={`Dépôts (${p.deposits_n})`} value={fmt(p.deposits)} testId="dash-deposits" /><Card label={`Retraits (${p.withdrawals_n})`} value={fmt(p.withdrawals)} testId="dash-withdrawals" />
      </div>
      <div className="surface mb-6 h-60 p-4">
        <ResponsiveContainer><BarChart data={d.series}><XAxis dataKey="day" stroke="#64748b" fontSize={11} /><YAxis stroke="#64748b" fontSize={11} /><Tooltip contentStyle={{ background: "#121620", border: 0 }} /><Bar dataKey="commission" fill="#f59e0b" name="Commission" /><Bar dataKey="games" fill="#10b981" name="Parties" /></BarChart></ResponsiveContainer>
      </div>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5" data-testid="dash-alerts">
        {Object.entries({ withdrawals_awaiting: "Retraits à valider", payments_stuck: "Paiements bloqués", payments_review: "Paiements à vérifier", kyc_pending: "KYC en attente", anomalies: "Anomalies" }).map(([k, l]) => (
          <div key={k} className={`surface p-4 ${d.alerts[k] ? "border-red-500/40" : ""}`}><p className="text-xs text-slate-400">{l}</p><p className={`font-mono text-xl ${d.alerts[k] ? "text-red-300" : ""}`}>{d.alerts[k]}</p></div>
        ))}
      </div>
    </div>
  );
};

const RegionCoverage = ({ region, setRegion }) => {
  const [period, setPeriod] = useState("month");
  const [d] = useLoad(`/kyc/regions?period=${period}`, [period]);
  if (!d) return null;
  return (
    <div className="surface mb-5 space-y-3 p-4" data-testid="kyc-regions">
      <p className="font-semibold">Régions couvertes : <span className="text-amber-300" data-testid="kyc-regions-covered">{d.covered} / {d.total}</span> <span className="text-xs font-normal text-slate-500">(au moins une identité confirmée)</span></p>
      <CameroonMap regions={d.regions} selected={region} onSelect={(r) => setRegion(region === r ? "" : r)} period={period} setPeriod={setPeriod} />
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
        {d.regions.map((r) => {
          const key = r.region || "none";
          return (
            <button key={key} type="button" onClick={() => setRegion(region === key ? "" : key)} data-testid={`kyc-region-${key}`}
              className={`rounded-xl border p-3 text-left text-xs transition-colors ${region === key ? "border-amber-500 bg-amber-500/10" : r.approved && r.region ? "border-emerald-500/40 bg-emerald-500/5" : "border-white/10"}`}>
              <p className="truncate text-sm font-semibold">{r.region || "Non renseignée"}</p>
              <p className="text-emerald-400">{r.approved} confirmée(s)</p>
              <p className="text-slate-400">{r.pending} en attente · {r.rejected} refusée(s)</p>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export const Players = ({ kycOnly }) => {
  const [q, setQ] = useState("");
  const [region, setRegion] = useState("");
  const [rows] = useLoad(`/players?q=${encodeURIComponent(q)}${kycOnly && !region ? "&kyc=pending" : ""}${region ? `&region=${encodeURIComponent(region)}` : ""}`, [q, region]);
  return (
    <div data-testid={kycOnly ? "admin-kyc" : "admin-players"}>
      <H>{kycOnly ? (region ? `Identités · ${region === "none" ? "région non renseignée" : region}` : "KYC en attente") : "Joueurs"}</H>
      {kycOnly && <RegionCoverage region={region} setRegion={setRegion} />}
      {!kycOnly && <Input placeholder="Pseudo, téléphone ou e-mail" value={q} onChange={(e) => setQ(e.target.value)} className="mb-4 h-11 max-w-sm rounded-xl border-white/10 bg-white/5" data-testid="admin-player-search" />}
      <Table testId="admin-players-table" rows={rows} cols={[
        ["Pseudo", (r) => <Link to={`/admin/players/${r.id}`} className="font-semibold text-amber-300" data-testid={`admin-player-link-${r.pseudo}`}>{r.pseudo}</Link>],
        ["Contact", (r) => r.email || r.phone], ["Statut", (r) => r.status], ["Identité", (r) => kycText(r.kyc_status)], ["Région", (r) => r.kyc_region || "—"],
        ["Disponible", (r) => fmt(r.wallet.available)], ["Bloqué", (r) => fmt(r.wallet.locked)], ["Inscrit", (r) => fdate(r.created_at)]]} />
    </div>
  );
};

export const PlayerDetail = () => {
  const { id } = useParams();
  const admin = useAdmin();
  const [d, reload] = useLoad(`/players/${id}`);
  const [temp, setTemp] = useState(null);
  if (!d) return null;
  const u = d.user;
  return (
    <div className="space-y-5" data-testid="admin-player-detail">
      <H>{u.pseudo}</H>
      {can(admin, "support") && (
        <div className="surface flex flex-wrap items-center justify-between gap-3 border border-amber-500/40 p-4" data-testid="pd-password-section">
          <div>
            <p className="font-semibold">Mot de passe oublié</p>
            <p className="text-xs text-slate-400">Crée un mot de passe provisoire à envoyer au joueur. Vérifiez d'abord que la demande vient bien de lui.</p>
          </div>
          <button type="button" data-testid="pd-reset-password" className="h-11 rounded-full bg-amber-500 px-5 font-bold text-[#0B0D12] hover:bg-amber-400" onClick={async () => {
            if (!window.confirm("Réinitialiser le mot de passe de ce joueur ? Ses sessions ouvertes seront fermées. Faites-le seulement après avoir vérifié que la demande vient bien de lui.")) return;
            try { const { data } = await adminApi.post(`/players/${id}/reset-password`); setTemp(data.temp_password); toast.success("Mot de passe réinitialisé"); } catch (e) { toast.error(aErr(e)); }
          }}>Réinitialiser le mot de passe</button>
        </div>
      )}
      {temp && (
        <div className="surface space-y-3 border border-emerald-500/30 p-4" data-testid="pd-temp-password-box">
          <p className="text-sm">Mot de passe provisoire (affiché une seule fois) :</p>
          <p className="font-mono text-2xl font-bold tracking-wider text-emerald-300" data-testid="pd-temp-password">{temp}</p>
          <p className="text-xs text-slate-400">Le joueur se connecte avec ce mot de passe, puis doit en choisir un nouveau immédiatement.</p>
          <div className="flex flex-wrap gap-2">
            <Btn testId="pd-temp-copy" onClick={() => navigator.clipboard?.writeText(temp).then(() => toast.success("Copié"))}>Copier</Btn>
            {u.phone && <a href={`https://wa.me/${u.phone.replace(/\D/g, "")}?text=${encodeURIComponent(`Bonjour ${u.pseudo}, voici votre mot de passe provisoire BKMPLAY : ${temp}\nConnectez-vous puis choisissez un nouveau mot de passe.`)}`}
              target="_blank" rel="noopener noreferrer" className="inline-flex h-9 items-center rounded-lg bg-emerald-600 px-3 text-sm font-semibold text-white" data-testid="pd-temp-whatsapp">Envoyer sur WhatsApp</a>}
          </div>
        </div>
      )}
      <KycPanel d={d} id={id} reload={reload} admin={admin} />
      {can(admin, "support") && <PlayerMessage id={id} />}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Card label="Disponible" value={fmt(d.wallet.available)} testId="pd-available" /><Card label="Bloqué" value={fmt(d.wallet.locked)} />
        <Card label="Statut" value={u.status} testId="pd-status" /><Card label="Identité" value={KYC_LABEL[u.kyc_status || "none"]} testId="pd-kyc" />
      </div>
      <div className="surface flex flex-wrap items-center justify-between gap-3 p-4" data-testid="pd-wager-section">
        <p className="text-sm">À jouer avant retrait : <b className="font-mono" data-testid="pd-to-wager">{fmt(d.to_wager || 0)}</b> <span className="text-xs text-slate-400">(chaque recharge doit être misée une fois en partie)</span></p>
        {can(admin, "finance") && d.to_wager > 0 && (
          <Btn testId="pd-wager-reset" onClick={() => { const r = ask("Motif de la remise à zéro"); if (r) act(() => adminApi.post(`/players/${id}/wager-reset`, { reason: r }), "Remis à zéro", reload); }}>Remettre à zéro</Btn>
        )}
      </div>
      <p className="text-sm text-slate-400">{u.email || u.phone} · né(e) le {u.birth_date} · limites : {JSON.stringify(u.limits || {})} · auto-exclusion : {u.self_excluded_until ? fdate(u.self_excluded_until) : "non"}</p>
      <div className="flex flex-wrap gap-2">
        {can(admin, "support") && <Btn tone="red" testId="pd-toggle-status" onClick={() => { const r = ask("Motif"); if (r) act(() => adminApi.post(`/players/${id}/status`, { status: u.status === "suspended" ? "active" : "suspended", reason: r }), "Statut modifié", reload); }}>{u.status === "suspended" ? "Réactiver" : "Suspendre"}</Btn>}
        {can(admin, "support") && <Btn tone="red" testId="pd-reset-limits" onClick={() => { const r = ask("Motif (demande du joueur)"); if (r) act(() => adminApi.post(`/players/${id}/reset-limits`, { reason: r }), "Limites réinitialisées", reload); }}>Réinitialiser les limites</Btn>}
        {admin.role === "super_admin" && <Btn testId="pd-adjust" onClick={() => { const a = parseInt(window.prompt("Montant (négatif pour débiter)") || "", 10); const r = Number.isInteger(a) && ask("Motif obligatoire"); if (r) act(() => adminApi.post(`/players/${id}/adjust`, { amount: a, reason: r }), "Solde ajusté", reload); }}>Ajuster le solde</Btn>}
      </div>
      <h2 className="font-semibold">Transactions</h2>
      <Table rows={d.ledger} cols={[["Type", (r) => r.type], ["Disponible", (r) => fmt(r.available_delta)], ["Bloqué", (r) => fmt(r.locked_delta)], ["Description", (r) => r.description], ["Date", (r) => fdate(r.created_at)]]} />
      <h2 className="font-semibold">Parties</h2>
      <Table rows={d.games} cols={[["Partie", (r) => <Link className="text-amber-300" to={`/admin/games/${r.id}`}>{r.id.slice(0, 8)}</Link>], ["Statut", (r) => r.status], ["Mise", (r) => fmt(r.stake)], ["Date", (r) => fdate(r.created_at)]]} />
      <h2 className="font-semibold">Connexions récentes</h2>
      <Table rows={d.logins} cols={[["Type", (r) => r.kind], ["IP", (r) => r.ip], ["Appareil", (r) => <span className="text-xs">{r.ua?.slice(0, 60)}</span>], ["Date", (r) => fdate(r.created_at)]]} />
    </div>
  );
};

export const Games = () => {
  const [status, setStatus] = useState("");
  const [rows] = useLoad(`/games${status ? `?status=${status}` : ""}`, [status]);
  return (
    <div data-testid="admin-games">
      <H csv="games">Parties de Dés</H>
      <div className="mb-4 flex flex-wrap gap-2">{["", "in_progress", "finished", "cancelled"].map((s) => <button key={s} onClick={() => setStatus(s)} className={`h-9 rounded-full px-4 text-sm ${status === s ? "bg-amber-500 text-[#0B0D12]" : "border border-white/10"}`} data-testid={`games-filter-${s || "all"}`}>{s || "Toutes"}</button>)}</div>
      <Table rows={rows} cols={[["Partie", (r) => <Link className="text-amber-300" to={`/admin/games/${r.id}`} data-testid={`admin-game-${r.id}`}>{r.id.slice(0, 8)}</Link>], ["Statut", (r) => r.status],
        ["Joueurs", (r) => r.players.map((p) => p.pseudo).join(", ")], ["Mise", (r) => fmt(r.stake)], ["Pot", (r) => fmt(r.pot)], ["Date", (r) => fdate(r.created_at)]]} />
    </div>
  );
};

export const GameDetail = () => {
  const { id } = useParams();
  const admin = useAdmin();
  const [g, reload] = useLoad(`/games/${id}`);
  if (!g) return null;
  const name = (uid) => g.players.find((p) => p.user_id === uid)?.pseudo || uid;
  return (
    <div className="space-y-4" data-testid="admin-game-detail">
      <H>Partie {id.slice(0, 8)} · {g.status}</H>
      <p className="text-sm">Ordre : {g.order.map(name).join(" → ")} · Gagnant(s) : {g.winners.map(name).join(", ") || "—"} · Pot {fmt(g.pot)} · Commission {g.commission_percent}%</p>
      <p className="break-all font-mono text-xs text-slate-400">hash {g.seed_hash}<br />graine {g.seed || "(cachée jusqu'à la fin)"}</p>
      {g.settlement && <p className="text-sm">Règlement : {JSON.stringify(g.settlement.payouts)} · plateforme {fmt(g.settlement.platform_total)}</p>}
      {can(admin, "support") && ["starting", "in_progress", "settling"].includes(g.status) && <Btn tone="red" testId="admin-game-cancel" onClick={() => { const r = ask("Motif de l'annulation"); if (r) act(() => adminApi.post(`/games/${id}/cancel`, { reason: r }), "Partie annulée, mises remboursées", reload); }}>Annuler et rembourser</Btn>}
      <Table rows={g.roll_log} cols={[["Manche", (r) => r.round], ["Joueur", (r) => name(r.player_id)], ["Dés", (r) => r.dice.join(" + ")], ["Auto", (r) => (r.auto ? "oui" : "non")], ["Heure", (r) => fdate(r.at)]]} />
    </div>
  );
};

export const Finance = () => {
  const admin = useAdmin();
  const [kind, setKind] = useState("");
  const [rows, reload] = useLoad(`/payments${kind ? `?kind=${kind}` : ""}`, [kind]);
  const [rec, setRec] = useState(null);
  const fin = can(admin, "finance");
  return (
    <div data-testid="admin-finance">
      <H csv="payments">Finances</H>
      <div className="mb-4 flex flex-wrap gap-2">
        {["", "deposit", "withdrawal"].map((k) => <button key={k} onClick={() => setKind(k)} className={`h-9 rounded-full px-4 text-sm ${kind === k ? "bg-amber-500 text-[#0B0D12]" : "border border-white/10"}`} data-testid={`fin-filter-${k || "all"}`}>{{ "": "Tout", deposit: "Dépôts", withdrawal: "Retraits" }[k]}</button>)}
        {fin && <button onClick={() => adminApi.get("/reconciliation?days=30").then((r) => setRec(r.data)).catch((e) => toast.error(aErr(e)))} className="h-9 rounded-full border border-white/10 px-4 text-sm" data-testid="fin-reconcile-btn">Rapprochement 30 j</button>}
        <button onClick={() => downloadCsv("ledger")} className="h-9 rounded-full border border-white/10 px-4 text-sm" data-testid="export-ledger">Registre CSV</button>
        <button onClick={() => downloadCsv("commissions")} className="h-9 rounded-full border border-white/10 px-4 text-sm" data-testid="export-commissions">Commissions CSV</button>
      </div>
      {rec && <div className="surface mb-4 p-4 text-sm" data-testid="fin-reconcile-result">{Object.entries(rec).map(([k, v]) => <p key={k}>{k === "deposit" ? "Dépôts" : "Retraits"} : plateforme {fmt(v.platform_successful)} ({v.count}) · confirmé opérateur {fmt(v.campay_confirmed)} ({v.campay_count}) · registre {fmt(v.ledger_total)} · {v.match ? "✓ concordant" : "⚠ écart"}</p>)}</div>}
      <Table rows={rows} cols={[["Type", (r) => r.kind], ["Statut", (r) => r.status], ["Joueur", (r) => <Link to={`/admin/players/${r.user_id}`} className="font-semibold text-amber-300" data-testid={`fin-player-${r.id}`}>{r.pseudo || "—"}</Link>],
        ["Identité", (r) => r.kind !== "withdrawal" ? "—" : <div className="space-y-1 text-xs" data-testid={`fin-kyc-${r.id}`}>
          {kycText(r.kyc_status)}
          {r.kyc && <p className="text-slate-300">{r.kyc.full_name}<br />{DOC_LABEL[r.kyc.doc_type] || r.kyc.doc_type} n° <span className="font-mono">{r.kyc.doc_number}</span></p>}
          {r.kyc && r.kyc_status === "pending" && r.status === "AWAITING_APPROVAL" && can(admin, "support") && <Btn testId={`fin-kyc-approve-${r.id}`} onClick={() => decideKyc(r.user_id, "approved", reload)}>Confirmer l'identité</Btn>}
        </div>],
        ["Montant", (r) => fmt(r.amount)], ["Numéro", (r) => r.phone], ["Réf. opérateur", (r) => <span className="font-mono text-xs">{r.campay_reference?.slice(0, 13) || "—"}</span>], ["Date", (r) => fdate(r.created_at)],
        ["Actions", (r) => fin && <div className="flex gap-1">
          {["PENDING", "INITIATING", "REVIEW", "FAILING"].includes(r.status) && <Btn testId={`fin-check-${r.id}`} onClick={() => act(() => adminApi.post(`/payments/${r.id}/check`), "Vérifié auprès de l'opérateur", reload)}>Vérifier le paiement</Btn>}
          {r.status === "AWAITING_APPROVAL" && <>{r.kyc_status === "approved"
            ? <Btn testId={`fin-approve-${r.id}`} onClick={() => act(() => adminApi.post(`/withdrawals/${r.id}/approve`), "Retrait envoyé", reload)}>Valider</Btn>
            : <span className="text-xs text-amber-300" data-testid={`fin-approve-blocked-${r.id}`}>Confirmez d'abord l'identité</span>}
            <Btn tone="red" testId={`fin-reject-${r.id}`} onClick={() => { const m = ask("Motif du refus"); if (m) act(() => adminApi.post(`/withdrawals/${r.id}/reject`, { reason: m }), "Refusé et remboursé", reload); }}>Refuser</Btn></>}
        </div>]]} />
    </div>
  );
};

export const LudoGames = () => {
  const admin = useAdmin();
  const [d, reload] = useLoad("/ludo/games");
  const [alerts, reloadAlerts] = useLoad("/ludo/alerts");
  const [sel, setSel] = useState(null);
  if (!d) return null;
  const SIG = { frequent_coplay: "Jouent très souvent ensemble (7 j)", same_device: "Même appareil / connexion", shared_deposit_phone: "Même numéro de recharge" };
  const open = async (id) => { try { setSel((await adminApi.get(`/ludo/games/${id}`)).data); } catch (e) { toast.error(aErr(e)); } };
  return (
    <div className="space-y-5" data-testid="admin-ludo">
      <H>Ludo</H>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Card label="Parties Ludo terminées" value={d.stats.games} testId="ludo-stat-games" />
        <Card label="Volume misé Ludo" value={fmt(d.stats.staked)} testId="ludo-stat-staked" />
        <Card label="Commissions Ludo" value={fmt(d.stats.commission)} testId="ludo-stat-commission" />
      </div>
      <p className="text-xs text-slate-500">Mises, modes, minuteur, durée, options et commission du Ludo : page Réglages (clés ludo_*).</p>
      <div className="surface space-y-2 p-4" data-testid="ludo-alerts">
        <p className="font-semibold">Alertes anti-entente ({(alerts || []).filter((a) => !a.resolved).length} ouvertes)</p>
        {(alerts || []).length === 0 && <p className="text-sm text-slate-500" data-testid="ludo-alerts-empty">Aucune alerte.</p>}
        {(alerts || []).map((a, i) => (
          <div key={i} className="flex flex-wrap items-center justify-between gap-2 rounded-xl bg-white/[0.03] px-3 py-2 text-sm" data-testid={`ludo-alert-${i}`}>
            <span><b>{a.data.pseudos?.join(" / ")}</b> · {SIG[a.data.signal] || a.data.signal} · {a.data.game_id} · {a.created_at?.slice(0, 16).replace("T", " ")}</span>
            {a.resolved ? <span className="text-xs text-slate-500">Traitée ({a.resolved_by})</span> : can(admin, "support") && (
              <Btn testId={`ludo-alert-resolve-${i}`} onClick={() => act(() => adminApi.post("/ludo/alerts/resolve", { pair: a.data.pair, signal: a.data.signal }), "Alerte traitée", reloadAlerts)}>Marquer traitée</Btn>
            )}
          </div>
        ))}
      </div>
      <Table testId="ludo-games-table" rows={d.games} cols={[
        ["Partie", (r) => <button className="font-mono text-amber-300" onClick={() => open(r.id)} data-testid={`ludo-open-${r.id}`}>{r.id}</button>],
        ["Joueurs", (r) => r.player_ids.map((p) => r.pseudos[p]).join(", ")], ["Mode", (r) => (r.mode === 2 ? "Rapide" : "Classique")],
        ["Mise", (r) => fmt(r.stake)], ["Statut", (r) => r.status], ["Abandons", (r) => r.forfeited.length], ["Salon", (r) => r.room_code || "—"], ["Signaux", (r) => (r.collusion_flags?.length ? <span className="text-red-300">{r.collusion_flags.length}</span> : "—")],
        ["", (r) => r.status === "in_progress" && can(admin, "support") && <Btn tone="red" testId={`ludo-cancel-${r.id}`} onClick={() => {
          const why = ask("Motif de l'annulation (remboursement de toutes les mises)");
          if (why) act(() => adminApi.post(`/ludo/games/${r.id}/cancel`, { reason: why }), "Partie annulée et remboursée", reload);
        }}>Annuler</Btn>],
      ]} />
      {sel && (
        <div className="surface space-y-2 p-4" data-testid="ludo-game-detail">
          <p className="font-mono text-sm">{sel.id} · graine {sel.seed || "cachée jusqu'à la fin"} · empreinte {sel.seed_hash.slice(0, 16)}…</p>
          <div className="max-h-80 overflow-auto font-mono text-xs text-slate-300">
            {sel.actions.map((a, i) => <p key={i}>{i + 1}. {sel.pseudos[a.by || a.player] || ""} {a.type}{a.die ? ` ${a.die}` : ""}{a.type === "move" ? ` pion ${a.piece} ${a.from}→${a.to}` : ""}{a.captured?.length ? " capture" : ""}{a.auto ? " (auto)" : ""}</p>)}
          </div>
        </div>
      )}
    </div>
  );
};

const DAMES_STATUS = { invited: "Invitation", accepting: "Acceptation", starting: "Départ", in_progress: "En cours", finished: "Terminée", cancelled: "Annulée", declined: "Refusée", expired: "Expirée" };
const DAMES_RESULT = { w: "Blancs gagnent", b: "Noirs gagnent", draw: "Nulle" };

const DamesSettings = () => {
  const admin = useAdmin();
  const [s, reload] = useLoad("/settings");
  const [f, setF] = useState(null);
  useEffect(() => {
    if (s) setF((old) => old && !old.saved ? old : { stakes: s.dames_stakes.join(", "), com: s.dames_commission_percent, inv: s.dames_invite_minutes, rule: s.dames_draw_rule, pub: s.dames_public_queue,
      bb: s.dames_cadences.blitz[0] / 60, bi: s.dames_cadences.blitz[1], rb: s.dames_cadences.rapide[0] / 60, ri: s.dames_cadences.rapide[1] });
  }, [s]);
  if (!f) return null;
  const ro = admin.role !== "super_admin";
  const num = (k, label) => (
    <label className="text-xs text-slate-400">{label}
      <Input type="number" value={f[k]} disabled={ro} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="mt-1 border-white/10 bg-white/5" data-testid={`dames-set-${k}`} />
    </label>
  );
  const save = () => act(() => adminApi.put("/settings", {
    dames_stakes: f.stakes.split(",").map((x) => parseInt(x.trim(), 10)).filter((x) => !Number.isNaN(x)),
    dames_commission_percent: +f.com, dames_invite_minutes: +f.inv, dames_draw_rule: f.rule, dames_public_queue: f.pub,
    dames_cadences: { blitz: [Math.round(f.bb * 60), +f.bi], rapide: [Math.round(f.rb * 60), +f.ri] },
  }), "Réglages Dames enregistrés", () => { setF({ ...f, saved: true }); reload(); });
  return (
    <div className="surface space-y-3 p-4" data-testid="dames-settings">
      <p className="font-semibold">Réglages Dames {ro && <span className="text-xs text-slate-500">(lecture seule, super-admin requis)</span>}</p>
      <label className="block text-xs text-slate-400">Mises autorisées (XAF, séparées par des virgules, minimum 250)
        <Input value={f.stakes} disabled={ro} onChange={(e) => setF({ ...f, stakes: e.target.value })} className="mt-1 border-white/10 bg-white/5" data-testid="dames-set-stakes" />
      </label>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {num("com", "Commission %")}{num("inv", "Validité invitation (min)")}
        {num("bb", "Blitz : minutes")}{num("bi", "Blitz : + s par coup")}{num("rb", "Rapide : minutes")}{num("ri", "Rapide : + s par coup")}
      </div>
      <div className="flex flex-wrap items-center gap-4 text-sm">
        <label className="flex items-center gap-2">Partie nulle :
          <select value={f.rule} disabled={ro} onChange={(e) => setF({ ...f, rule: e.target.value })} className="h-9 rounded-lg border border-white/10 bg-[#121620] px-2" data-testid="dames-set-rule">
            <option value="split">Pot partagé (moins commission)</option><option value="refund">Mises remboursées</option>
          </select>
        </label>
        <label className="flex items-center gap-2"><input type="checkbox" checked={f.pub} disabled={ro} onChange={(e) => setF({ ...f, pub: e.target.checked })} data-testid="dames-set-public" /> Recherche d'adversaire au hasard (bientôt)</label>
      </div>
      {!ro && <Btn testId="dames-settings-save" onClick={save}>Enregistrer</Btn>}
    </div>
  );
};

const DamesDetail = ({ g }) => (
  <div className="surface space-y-2 p-4" data-testid="dames-game-detail">
    <p className="font-mono text-sm">{g.id} · {g.pseudos[g.white_id] || "?"} (blancs) vs {g.pseudos[g.black_id] || "?"} (noirs) · {g.cadence}</p>
    <p className="text-sm text-slate-400">Statut {DAMES_STATUS[g.status] || g.status} · {DAMES_RESULT[g.result] || "—"} · motif {g.reason || g.cancel_reason || "—"} · mise {fmt(g.stake)} · pot {fmt(g.pot)}
      {g.settlement && <> · commission {fmt(g.settlement.platform_total ?? g.settlement.commission)} · versé {Object.entries(g.settlement.payouts).map(([u, a]) => `${g.pseudos[u]} ${fmt(a)}`).join(", ")}</>}</p>
    <p className="font-mono text-xs text-slate-500">Créée {fdate(g.created_at)} · code {g.code} · empreinte {g.seed_hash?.slice(0, 16)}…</p>
    <div className="max-h-72 overflow-auto font-mono text-xs text-slate-300" data-testid="dames-detail-moves">
      {(g.moves || []).length === 0 ? <p>Aucun coup joué.</p> : g.moves.map((m, i) => <p key={i}>{Math.floor(i / 2) + 1}{i % 2 ? "…" : "."} {m.notation}{m.promoted ? " (dame)" : ""}{m.at ? ` · ${m.at.slice(11, 19)}` : ""}</p>)}
    </div>
  </div>
);

export const DamesGames = () => {
  const admin = useAdmin();
  const [flt, setFlt] = useState({ status: "", pseudo: "" });
  const q = new URLSearchParams(Object.entries(flt).filter(([, v]) => v)).toString();
  const [d, reload] = useLoad(`/dames/games${q ? `?${q}` : ""}`);
  const [sel, setSel] = useState(null);
  const open = async (id) => {
    try { setSel((await adminApi.get(`/dames/games/${id}`)).data); setTimeout(() => document.querySelector("[data-testid='dames-game-detail']")?.scrollIntoView({ behavior: "smooth" }), 50); } catch (e) { toast.error(aErr(e)); }
  };
  if (!d) return null;
  return (
    <div className="space-y-5" data-testid="admin-dames">
      <H>Dames</H>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <Card label="Parties terminées" value={d.stats.games} testId="dames-stat-games" />
        <Card label="Volume misé" value={fmt(d.stats.staked)} testId="dames-stat-staked" />
        <Card label="Commissions" value={fmt(d.stats.commission)} testId="dames-stat-commission" />
        <Card label="Nulles" value={d.stats.draws} testId="dames-stat-draws" />
        <Card label="En cours" value={d.stats.live} testId="dames-stat-live" />
        <Card label="Invitations ouvertes" value={d.stats.invites} testId="dames-stat-invites" />
      </div>
      <div className="surface space-y-2 p-4" data-testid="dames-signals">
        <p className="font-semibold">Signaux à surveiller (même paire, 5 parties ou plus en 7 jours)</p>
        {d.signals.length === 0 ? <p className="text-sm text-slate-500" data-testid="dames-signals-empty">Aucun signal.</p> : d.signals.map((s, i) => (
          <p key={i} className="text-sm" data-testid={`dames-signal-${i}`}><b>{s.pseudos.join(" / ")}</b> · {s.games} parties · {s.resigns} abandons</p>
        ))}
      </div>
      <DamesSettings />
      <div className="flex flex-wrap gap-2">
        <select value={flt.status} onChange={(e) => setFlt({ ...flt, status: e.target.value })} className="h-10 rounded-lg border border-white/10 bg-[#121620] px-2 text-sm" data-testid="dames-filter-status">
          <option value="">Tous les statuts</option>{Object.entries(DAMES_STATUS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <Input placeholder="Pseudo exact" defaultValue={flt.pseudo} onKeyDown={(e) => { if (e.key === "Enter") setFlt({ ...flt, pseudo: e.target.value.trim() }); }}
          className="h-10 max-w-xs border-white/10 bg-white/5" data-testid="dames-filter-pseudo" />
      </div>
      <Table testId="dames-games-table" rows={d.games} cols={[
        ["Partie", (r) => <button className="font-mono text-amber-300" onClick={() => open(r.id)} data-testid={`dames-open-${r.id}`}>{r.id.slice(0, 8)}</button>],
        ["Joueurs", (r) => Object.values(r.pseudos).join(" vs ")], ["Cadence", (r) => r.cadence], ["Mise", (r) => fmt(r.stake)],
        ["Statut", (r) => DAMES_STATUS[r.status] || r.status], ["Résultat", (r) => DAMES_RESULT[r.result] || "—"], ["Date", (r) => fdate(r.created_at)],
        ["", (r) => ["invited", "starting", "in_progress"].includes(r.status) && can(admin, "support") && <Btn tone="red" testId={`dames-cancel-${r.id}`} onClick={() => {
          const why = ask("Motif de l'annulation (obligatoire, mises remboursées)");
          if (why) act(() => adminApi.post(`/dames/games/${r.id}/cancel`, { reason: why }), "Partie annulée et remboursée", () => { reload(); if (sel?.id === r.id) open(r.id); });
        }}>Annuler</Btn>],
      ]} />
      {sel && <DamesDetail g={sel} />}
    </div>
  );
};


export const Settings = () => {
  const admin = useAdmin();
  const [s, reload] = useLoad("/settings");
  const [text, setText] = useState("");
  useEffect(() => {
    if (s) setText(JSON.stringify(Object.fromEntries(Object.entries(s).filter(([k]) => k !== "key")), null, 2));
  }, [s]);
  const save = () => {
    let body;
    try {
      body = JSON.parse(text);
    } catch {
      return toast.error("JSON invalide");
    }
    return act(() => adminApi.put("/settings", body), "Réglages enregistrés", reload);
  };
  return (
    <div data-testid="admin-settings">
      <H>Réglages du jeu</H>
      <p className="mb-3 text-sm text-slate-400">Mises (minimum 25), nombres de joueurs, commission (globale ou par nombre de joueurs, ex. {"{\"2\": 10}"}), délais, limites de dépôt/retrait, mode paiement (DEV/PROD), maintenance. Chaque changement est inscrit au journal d'audit.</p>
      <textarea value={text} onChange={(e) => setText(e.target.value)} readOnly={admin.role !== "super_admin"} rows={24} className="w-full rounded-xl border border-white/10 bg-black/30 p-4 font-mono text-xs" data-testid="settings-json" />
      {admin.role === "super_admin" && <button onClick={save} className="mt-3 h-11 rounded-full bg-amber-500 px-6 font-semibold text-[#0B0D12]" data-testid="settings-save-btn">Enregistrer</button>}
    </div>
  );
};

export const ReferralAdmin = () => {
  const admin = useAdmin();
  const [d, reload] = useLoad("/referral");
  const [s, reloadS] = useLoad("/settings");
  const [f, setF] = useState(null);
  useEffect(() => { if (s) setF({ pct: s.referral_percent, cap: s.referral_monthly_cap, min: s.referral_min_transfer }); }, [s]);
  if (!d || !s || !f) return null;
  const sa = admin.role === "super_admin";
  const save = (patch) => act(() => adminApi.put("/settings", patch), "Parrainage mis à jour", () => { reloadS(); reload(); });
  const block = (r, blocked) => {
    const reason = ask(blocked ? "Motif du blocage (obligatoire)" : "Motif du déblocage (obligatoire)");
    if (!reason) return;
    const void_balance = blocked && r.balance > 0 && window.confirm(`Annuler aussi les gains non transférés de ${r.pseudo} (${fmt(r.balance)}) ?`);
    act(() => adminApi.post(`/referral/${r.user_id}/block`, { reason, blocked, void_balance }), blocked ? "Parrain bloqué" : "Parrain débloqué", reload);
  };
  return (
    <div className="space-y-5" data-testid="admin-referral">
      <H>Parrainage</H>
      <div className="surface flex flex-wrap items-center gap-3 p-4">
        <p className="flex-1 text-sm">Programme : <b data-testid="referral-state">{s.referral_enabled ? "activé" : "coupé"}</b></p>
        {sa && <Btn tone={s.referral_enabled ? "red" : undefined} testId="referral-toggle-btn" onClick={() => save({ referral_enabled: !s.referral_enabled })}>{s.referral_enabled ? "Couper le programme" : "Activer le programme"}</Btn>}
      </div>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <Card label="Coût ce mois" value={fmt(d.month_cost)} testId="referral-stat-cost" />
        <Card label="Mises des filleuls (mois)" value={fmt(d.month_stakes)} testId="referral-stat-stakes" />
        <Card label="Parties des filleuls (mois)" value={d.month_games} testId="referral-stat-games" />
        <Card label="Filleuls inscrits" value={d.filleuls} testId="referral-stat-filleuls" />
        <Card label="Total transféré" value={fmt(d.paid_total)} testId="referral-stat-paid" />
      </div>
      <div className="surface grid grid-cols-1 gap-3 p-4 sm:grid-cols-4" data-testid="referral-settings">
        {[["pct", "% de la commission"], ["cap", "Plafond mensuel (XAF)"], ["min", "Seuil de transfert (XAF)"]].map(([k, l]) => (
          <label key={k} className="text-xs text-slate-400">{l}
            <Input type="number" value={f[k]} disabled={!sa} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="mt-1 border-white/10 bg-white/5" data-testid={`referral-set-${k}`} />
          </label>
        ))}
        {sa && <div className="flex items-end"><Btn testId="referral-settings-save" onClick={() => save({ referral_percent: +f.pct, referral_monthly_cap: +f.cap, referral_min_transfer: +f.min })}>Enregistrer</Btn></div>}
      </div>
      <Table testId="referral-sponsors-table" rows={d.sponsors} cols={[
        ["Parrain", (r) => <Link to={`/admin/players/${r.user_id}`} className="text-amber-300">{r.pseudo}</Link>], ["Région", (r) => r.region || "—"],
        ["Filleuls", (r) => r.filleuls], ["Ce mois", (r) => fmt(r.month)], ["À transférer", (r) => fmt(r.balance)], ["Total gagné", (r) => fmt(r.total)],
        ["Statut", (r) => (r.blocked ? "Bloqué" : "Actif")],
        ["", (r) => can(admin, "support") && <Btn tone={r.blocked ? undefined : "red"} testId={`referral-block-${r.user_id}`} onClick={() => block(r, !r.blocked)}>{r.blocked ? "Débloquer" : "Bloquer"}</Btn>],
      ]} />
    </div>
  );
};

const KIND_PATH = { dice: "games", ludo: "ludo", dames: "dames" };

export const ChatReports = () => {
  const admin = useAdmin();
  const [status, setStatus] = useState("open");
  const [d, reload] = useLoad(`/chat/reports?status=${status}`);
  if (!d) return null;
  const toggle = () => act(() => adminApi.put("/settings", { chat_enabled: !d.chat_enabled }), d.chat_enabled ? "Chat coupé pour tous" : "Chat réactivé", reload);
  const resolve = (r, action) => act(() => adminApi.post(`/chat/reports/${r.id}/resolve`, { action }), action === "hide" ? "Message masqué" : "Signalement classé", reload);
  return (
    <div className="space-y-5" data-testid="admin-chat">
      <H>Chat des parties</H>
      <div className="surface flex flex-wrap items-center gap-3 p-4" data-testid="chat-switch">
        <p className="flex-1 text-sm">Chat pendant les parties : <b data-testid="chat-switch-state">{d.chat_enabled ? "activé" : "coupé"}</b></p>
        {admin.role === "super_admin" && <Btn tone={d.chat_enabled ? "red" : undefined} testId="chat-switch-btn" onClick={toggle}>{d.chat_enabled ? "Couper le chat pour tous" : "Réactiver le chat"}</Btn>}
      </div>
      <select value={status} onChange={(e) => setStatus(e.target.value)} className="h-10 rounded-lg border border-white/10 bg-[#121620] px-2 text-sm" data-testid="chat-reports-filter">
        <option value="open">Signalements ouverts</option><option value="resolved">Traités</option><option value="all">Tous</option>
      </select>
      {d.reports.length === 0 && <p className="text-sm text-slate-500" data-testid="chat-reports-empty">Aucun signalement.</p>}
      {d.reports.map((r) => (
        <div key={r.id} className="surface space-y-2 p-4" data-testid={`chat-report-${r.id}`}>
          <p className="text-sm"><b>{r.message.pseudo}</b> signalé par <b>{r.reporter_pseudo}</b> · {fdate(r.created_at)} · partie {r.message.game_kind}{" "}
            <Link to={`/admin/${KIND_PATH[r.message.game_kind]}`} className="font-mono text-amber-300">{r.message.game_id.slice(0, 8)}</Link></p>
          <p className="rounded-lg bg-red-500/10 px-3 py-2 text-sm" data-testid={`chat-report-text-${r.id}`}>« {r.message.text} »{r.message.hidden && " (masqué)"}</p>
          {r.reason && <p className="text-xs text-slate-400">Motif : {r.reason}</p>}
          <div className="space-y-0.5 font-mono text-xs text-slate-500">{r.context.map((c) => <p key={c.id}>{c.created_at.slice(11, 19)} {c.pseudo} : {c.text}</p>)}</div>
          {r.status === "open" ? can(admin, "support") && (
            <div className="flex gap-2">
              <Btn tone="red" testId={`chat-hide-${r.id}`} onClick={() => resolve(r, "hide")}>Masquer le message</Btn>
              <Btn testId={`chat-dismiss-${r.id}`} onClick={() => resolve(r, "dismiss")}>Classer sans suite</Btn>
            </div>
          ) : <p className="text-xs text-slate-500">Traité par {r.resolved_by} · {r.action === "hide" ? "message masqué" : "classé"}</p>}
        </div>
      ))}
    </div>
  );
};


export const Announcements = () => {
  const admin = useAdmin();
  const [rows, reload] = useLoad("/announcements");
  const [f, setF] = useState({ message_fr: "", message_en: "", kind: "info" });
  return (
    <div data-testid="admin-announcements">
      <H>Annonces</H>
      {can(admin, "support") && <div className="surface mb-4 space-y-2 p-4">
        <Input placeholder="Message (FR)" value={f.message_fr} onChange={(e) => setF({ ...f, message_fr: e.target.value })} className="border-white/10 bg-white/5" data-testid="ann-fr-input" />
        <Input placeholder="Message (EN, facultatif)" value={f.message_en} onChange={(e) => setF({ ...f, message_en: e.target.value })} className="border-white/10 bg-white/5" data-testid="ann-en-input" />
        <div className="flex gap-2">
          <select value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })} className="h-9 rounded-lg border border-white/10 bg-[#121620] px-2 text-sm" data-testid="ann-kind-select"><option value="info">Info</option><option value="maintenance">Maintenance</option></select>
          <Btn testId="ann-create-btn" onClick={() => act(() => adminApi.post("/announcements", { ...f, active: true }), "Annonce publiée", () => { setF({ message_fr: "", message_en: "", kind: "info" }); reload(); })}>Publier</Btn>
        </div>
      </div>}
      <Table rows={rows} cols={[["Message", (r) => r.message_fr], ["Type", (r) => r.kind], ["Active", (r) => (r.active ? "oui" : "non")], ["Date", (r) => fdate(r.created_at)],
        ["", (r) => can(admin, "support") && <Btn tone="red" testId={`ann-toggle-${r.id}`} onClick={() => act(() => adminApi.patch(`/announcements/${r.id}`, { message_fr: r.message_fr, message_en: r.message_en || "", kind: r.kind, active: !r.active }), "Mis à jour", reload)}>{r.active ? "Désactiver" : "Activer"}</Btn>]]} />
    </div>
  );
};

export const Audit = () => {
  const [rows] = useLoad("/audit");
  return (
    <div data-testid="admin-audit">
      <H csv="audit">Journal d'audit</H>
      <Table rows={rows} cols={[["Date", (r) => fdate(r.created_at)], ["Admin", (r) => r.admin_email || "—"], ["Action", (r) => r.action], ["Cible", (r) => <span className="font-mono text-xs">{r.target}</span>], ["Détails", (r) => <span className="text-xs">{JSON.stringify(r.data)}</span>]]} />
    </div>
  );
};

export const Admins = () => {
  const me = useAdmin();
  const [rows, reload] = useLoad("/admins");
  const [f, setF] = useState({ email: "", name: "", role: "support", password: "" });
  return (
    <div data-testid="admin-admins">
      <H>Administrateurs</H>
      <div className="surface mb-4 grid gap-2 p-4 sm:grid-cols-5">
        <Input placeholder="E-mail" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="border-white/10 bg-white/5" data-testid="new-admin-email" />
        <Input placeholder="Nom" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="border-white/10 bg-white/5" data-testid="new-admin-name" />
        <select value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} className="h-10 rounded-lg border border-white/10 bg-[#121620] px-2 text-sm" data-testid="new-admin-role">{["super_admin", "finance", "support", "readonly"].map((r) => <option key={r} value={r}>{r}</option>)}</select>
        <Input placeholder="Mot de passe provisoire" type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className="border-white/10 bg-white/5" data-testid="new-admin-password" />
        <Btn testId="new-admin-create" onClick={() => act(() => adminApi.post("/admins", f), "Admin créé (changement de mot de passe + 2FA à la 1re connexion)", reload)}>Créer</Btn>
      </div>
      <Table rows={rows} cols={[["E-mail", (r) => r.email], ["Rôle", (r) => r.role], ["2FA", (r) => (r.totp_enabled ? "activée" : "à configurer")], ["Statut", (r) => r.status], ["Dernière connexion", (r) => fdate(r.last_login_at)],
        ["", (r) => r.id !== me.id && <Btn tone="red" testId={`admin-toggle-${r.email}`} onClick={() => act(() => adminApi.patch(`/admins/${r.id}`, { status: r.status === "active" ? "disabled" : "active" }), "Mis à jour", reload)}>{r.status === "active" ? "Désactiver" : "Activer"}</Btn>]]} />
    </div>
  );
};
